import JSZip from 'jszip';

export interface StagedFile {
  id: string;
  file: File | Blob;
  name: string;
  path: string;
  status: 'ok' | 'error' | 'corrupt' | 'pending';
  errorMessage?: string;
}

export interface StagedSupplier {
  id: string;
  supplierName: string;
  files: StagedFile[];
}

const SUPPORTED_EXTS = [
  '.pdf',
  '.doc',
  '.docx',
  '.txt',
  '.md',
  '.xls',
  '.xlsx',
  '.csv',
  '.ppt',
  '.pptx',
];

export const processDroppedFiles = async (droppedFiles: File[]): Promise<StagedSupplier[]> => {
  const suppliersMap = new Map<string, StagedSupplier>();

  const getOrCreateSupplier = (name: string) => {
    if (!suppliersMap.has(name)) {
      suppliersMap.set(name, {
        id: crypto.randomUUID(),
        supplierName: name,
        files: [],
      });
    }
    return suppliersMap.get(name) as StagedSupplier;
  };

  const validateFile = (name: string): { status: 'ok' | 'error'; errorMessage?: string } => {
    const ext = name.substring(name.lastIndexOf('.')).toLowerCase();
    if (!SUPPORTED_EXTS.includes(ext)) {
      return {
        status: 'error',
        errorMessage: `Unsupported file type. Allowed types: ${SUPPORTED_EXTS.join(', ')}`,
      };
    }
    return { status: 'ok' };
  };

  for (const file of droppedFiles) {
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (ext === '.zip') {
      try {
        const zip = new JSZip();
        const zipContent = await zip.loadAsync(file);
        const supplierNameFromZip = file.name.replace(/\.zip$/i, '');

        for (const [relativePath, zipEntry] of Object.entries(zipContent.files)) {
          if (zipEntry.dir) {
            continue;
          }

          // Ignore macOS hidden files
          if (
            relativePath.includes('__MACOSX/') ||
            relativePath.split('/').pop()?.startsWith('.')
          ) {
            continue;
          }

          const parts = relativePath.split('/');
          const supplierName = supplierNameFromZip;
          const fileName = parts[parts.length - 1] || 'unknown-file';

          const blob = await zipEntry.async('blob');

          let mimeType = blob.type;
          if (!mimeType) {
            const extLower = ext.toLowerCase();
            const mimeTypes: Record<string, string> = {
              '.pdf': 'application/pdf',
              '.doc': 'application/msword',
              '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              '.txt': 'text/plain',
              '.md': 'text/markdown',
              '.xls': 'application/vnd.ms-excel',
              '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              '.csv': 'text/csv',
              '.ppt': 'application/vnd.ms-powerpoint',
              '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            };
            mimeType = mimeTypes[extLower] || 'application/octet-stream';
          }

          const extractedFile = new File([blob], fileName, { type: mimeType });

          const supplier = getOrCreateSupplier(supplierName);
          const validation = validateFile(fileName);

          const stagedFile: StagedFile = {
            id: crypto.randomUUID(),
            file: extractedFile,
            name: fileName,
            path: relativePath,
            status: validation.status,
          };

          if (validation.errorMessage) {
            stagedFile.errorMessage = validation.errorMessage;
          }

          supplier.files.push(stagedFile);
        }
      } catch (_e) {
        // Zip is corrupt
        const supplier = getOrCreateSupplier(file.name);
        supplier.files.push({
          id: crypto.randomUUID(),
          file,
          name: file.name,
          path: file.name,
          status: 'corrupt',
          errorMessage: 'Failed to extract ZIP archive. The file may be corrupt.',
        });
      }
    } else {
      // Normal file
      let supplierName = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
      if (file.webkitRelativePath) {
        const parts = file.webkitRelativePath.split('/');
        if (parts.length > 1 && parts[0]) {
          supplierName = parts[0];
        }
      }

      const supplier = getOrCreateSupplier(supplierName);
      const validation = validateFile(file.name);

      const stagedFile: StagedFile = {
        id: crypto.randomUUID(),
        file,
        name: file.name,
        path: file.webkitRelativePath || file.name,
        status: validation.status,
      };

      if (validation.errorMessage) {
        stagedFile.errorMessage = validation.errorMessage;
      }

      supplier.files.push(stagedFile);
    }
  }

  return Array.from(suppliersMap.values());
};
